<script lang="ts">
const __DsBlock = {
	icon: 'lucide:baseline',
	description: '$t:blocks.text.description',
	category: 'basic',
	options: [
		{
			name: 'text',
			label: '$t:blocks.common.props.text',
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: [...TEXT_SIZES],
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				autoTitleCaseValue: false,
				maxItems: 5,
				options: TEXT_SIZES.map((value) => ({
					label: `$t:blocks.text.size.${value}`,
					value,
				})),
			},
			slots: {
				'item-icon': ({ item }: { item: { value: string } }) => h('div', {
					class: `text-${item.value} overflow-hidden size-full flex items-center justify-center`,
				}, 'Aa'),
			},
		},
		{
			name: 'weight',
			label: '$t:blocks.common.props.weight',
			type: 'enum',
			values: TEXT_WEIGHTS.map((entry) => entry.value),
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				autoTitleCaseValue: false,
				options: TEXT_WEIGHTS.map((entry) => ({
					label: `$t:blocks.text.weight.${entry.value}`,
					value: entry.value,
					icon: entry.icon,
				})),
			},
			slots: {
				'item-icon': ({ item }: { item: { value: string } }) => h('div', {
					class: `font-${item.value}`,
				}, 'Aa'),
			},
		},
		{
			name: 'align',
			label: '$t:blocks.common.props.align',
			type: 'enum',
			values: TEXT_ALIGNS.map((entry) => entry.value),
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				autoTitleCaseValue: false,
				options: TEXT_ALIGNS.map((entry) => ({
					label: `$t:blocks.text.align.${entry.value}`,
					value: entry.value,
					icon: entry.icon,
				})),
			},
		},
	],
}
</script>
<script lang="ts" setup>
import type { TextAlign, TextSize, TextWeight } from '#layers/blocks/constants/text-tokens';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed, h } from 'vue';
import { defineBlock, useBlockInEditorContext } from '#layers/block-engine/public';
import { TEXT_ALIGNS, TEXT_SIZES, TEXT_WEIGHTS } from '#layers/blocks/constants/text-tokens';
import { useTranslationUtils } from '#layers/i18n/public';

const {
	text = '$t:blocks.text.default_text',
	size = 'md',
	weight = 'normal',
	align = 'start',
} = defineProps<{
	/**
	 * The text to display.
	 */
	text?: string;

	size?: TextSize;
	weight?: TextWeight;
	align?: TextAlign;
}>();

const { blockInEditor, setProp } = useBlockInEditorContext<{ text: string }>();
const { translateString } = useTranslationUtils();

const translatedText = computed(() => translateString(text));

const textClass = computed(() => [
	size !== undefined ? `text-${size}` : null,
	weight !== undefined ? `font-${weight}` : null,
	align !== undefined ? `text-${align}` : null,
]);

function handleTextInput(event: Event) {
	setProp('text', (event.target as HTMLTextAreaElement).value);
}

;
</script>

<template>
	<BlockInteractiveZone v-if="blockInEditor">
		<textarea
			:value="translatedText"
			:aria-label="$t('blocks.common.props.text')"
			rows="1"
			class="
				field-sizing-content w-full resize-none overflow-hidden border-0 bg-transparent p-0 ring-0 outline-none
				focus:ring-0 focus:outline-none
			"
			:class="textClass"
			@input="handleTextInput"
		/>
	</BlockInteractiveZone>
	<div
		v-else
		class="whitespace-pre-wrap"
		:class="textClass"
		v-text="translatedText"
	/>
</template>
